import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const StyledBox = styled('div')(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.default,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  textAlign: 'center', // Center text and headers
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 36px rgba(0, 0, 0, 0.15)',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(6),
  },
}));

const integrationGroups = [
  {
    title: 'Social Media',
    description: 'Automate content creation, scheduling and engagement across major social platforms.',
    icons: ['/icons/x.svg', '/icons/reddit.svg', '/icons/linkedin.svg', '/icons/facebook.svg'],
  },
  {
    title: 'Blockchain and Crypto',
    description: 'Power your blockchain projects with AI-driven development and trading.',
    icons: ['/icons/cardano.svg', '/icons/ethereum.svg', '/icons/solana.svg', '/icons/arkm.svg'],
  },
  {
    title: 'Messaging Platforms',
    description: 'Integrate Viva with messaging tools for analysis, real-time updates and notifications.',
    icons: ['/icons/telegram.svg', '/icons/discord.svg', '/icons/slack.svg', '/icons/teams.svg'],
  },
];

export default function Integrations() {
  return (
    <Box
      id="integrations"
      sx={(theme) => ({
        width: '100%',
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        paddingTop: theme.spacing(8),
        paddingBottom: theme.spacing(8),
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Container
        sx={{
          maxWidth: 'lg', // Ensure the content is centered on large screens
          textAlign: 'center', // Center the content within the section
        }}
      >
        <Typography
          component="h2"
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          Integrations
        </Typography>
        <Typography 
          variant="body1" 
          sx={{
            marginBottom: 4,
            color: 'grey.400'
          }}
        >
          Explore how Viva integrates into your ecosystem to enhance AI capabilities.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 4, // Spacing between cards
            margin: 0,
            padding: 0,
          }}
        >
          {integrationGroups.map((group, index) => (
            <Box
              key={index}
              sx={{
                width: { xs: '100%', sm: '48%', md: '30%' }, // Adjust widths for responsiveness
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <StyledBox>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: 2,
                    color: 'text.primary',
                  }}
                >
                  {group.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    marginBottom: 3,
                    color: 'text.secondary',
                    lineHeight: 1.8,
                  }}
                >
                  {group.description}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 4, // Increased gap between icons
                    flexWrap: 'wrap',
                  }}
                >
                  {group.icons.map((icon, iconIndex) => (
                    <Box
                      key={iconIndex}
                      component="img"
                      src={icon}
                      alt={`${group.title} Icon ${iconIndex + 1}`}
                      sx={{
                        width: 28, // Smaller icons
                        height: 28,
                        objectFit: 'contain',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </StyledBox>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}